import type { PostApiV1SelectionsSelectionIdApplicantResume200 } from "@/api/recrutaAI/generated/aISaaSAPI.schemas";
import { Loading } from "@/components/ui/loading";
import { SEX_PHRASE } from "@/constants/phrase/sex";
import { RecruitPositionGenerateModal } from "@/features/recruitPosition/components/recruitPositionGenerateModal";
import { RecruitPositionList } from "@/features/recruitPosition/components/recruitPositionList";
import { RecruitPositionStatistics } from "@/features/recruitPosition/components/recruitPositionStatistics";
import { AiDocumentSelectionResultCloseConfirmationDialog } from "@/features/recruitee/components/aiDocumentSelectionResultCloseConfirmationDialog";
import { RecruiteeDetail } from "@/features/recruitee/components/recruiteeDetail";
import {
	Avatar,
	Button,
	CloseIcon,
	Container,
	Drawer,
	Flex,
	ScrollArea,
	Text,
	Title,
	useMantineTheme,
} from "@mantine/core";
import { Stack } from "@mantine/core";
import { useDebouncedCallback, useDisclosure } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";
import { useIsMutating } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useGetApiV1SelectionsJobInformationSuspense } from "../../api/recrutaAI/generated/aISaaSAPI";

export const RecruitPositionsPage = () => {
	const theme = useMantineTheme();

	const [
		recruiteeDetailDrawerOpened,
		{ open: recruiteeDetailDrawerOpen, close: closeDrawer },
	] = useDisclosure(false);

	const [
		aiDocumentSelectionResultCloseConfirmationDialogOpened,
		{
			open: aiDocumentSelectionResultCloseConfirmationDialogOpen,
			close: aiDocumentSelectionResultCloseConfirmationDialogClose,
		},
	] = useDisclosure(false);

	const [resume, setResume] =
		useState<PostApiV1SelectionsSelectionIdApplicantResume200 | null>(null);

	const [
		recruitPositionGenerateModalOpened,
		{
			open: recruitPositionGenerateModalOpen,
			close: recruitPositionGenerateModalClose,
		},
	] = useDisclosure(false);

	const {
		data: getApiV1SelectionsJobInformationResponse,
		refetch: refetchJobInformation,
	} = useGetApiV1SelectionsJobInformationSuspense({
		offset: 0,
		limit: 1000,
	});

	const debouncedRefetchJobInformation = useDebouncedCallback(
		refetchJobInformation,
		1000,
	);

	const isMutating = useIsMutating();

	const handleRecruiteeDetailDrawerClose = useCallback(() => {
		closeDrawer();
		setResume(null);
	}, [closeDrawer]);

	return (
		<>
			<Container size="xl" w="100%">
				<Stack gap={24}>
					<Flex justify="space-between" align="center">
						<Title order={2}>選考基準</Title>
						<Button
							leftSection={<IconPlus size={16} />}
							onClick={recruitPositionGenerateModalOpen}
							disabled={
								getApiV1SelectionsJobInformationResponse.totalJobInformationCount ===
								getApiV1SelectionsJobInformationResponse.limitJobInformationCount
							}
						>
							<Text>選考基準を生成</Text>
						</Button>
					</Flex>
					<RecruitPositionStatistics
						recruitPositionCount={
							getApiV1SelectionsJobInformationResponse.totalJobInformationCount
						}
						maxRecruitPositionCount={
							getApiV1SelectionsJobInformationResponse.limitJobInformationCount
						}
						aiSelectionCount={
							getApiV1SelectionsJobInformationResponse.totalResumeCount
						}
						maxAiSelectionCount={
							getApiV1SelectionsJobInformationResponse.limitResumeCount
						}
					/>
					<RecruitPositionList
						recruitPositions={
							getApiV1SelectionsJobInformationResponse.jobInformation
						}
						handleReload={debouncedRefetchJobInformation}
						recruiteeDetailDrawerOpen={recruiteeDetailDrawerOpen}
						setResume={setResume}
						disabledAiDocumentSelection={
							getApiV1SelectionsJobInformationResponse.totalResumeCount ===
							getApiV1SelectionsJobInformationResponse.limitResumeCount
						}
					/>
					<Drawer.Root
						opened={recruiteeDetailDrawerOpened}
						onClose={() => {
							if (isMutating) {
								return;
							}
							if (resume) {
								aiDocumentSelectionResultCloseConfirmationDialogOpen();
							} else {
								handleRecruiteeDetailDrawerClose();
							}
						}}
						position="right"
						size="50%"
						scrollAreaComponent={ScrollArea.Autosize}
					>
						<Drawer.Overlay
							onClick={() => {
								if (isMutating) {
									return;
								}
								if (resume) {
									aiDocumentSelectionResultCloseConfirmationDialogOpen();
								} else {
									handleRecruiteeDetailDrawerClose();
								}
							}}
						/>
						<Drawer.Content>
							{isMutating ? (
								<Loading height="100vh" message="AIが書類選考中..." />
							) : resume ? (
								<>
									<Drawer.Header>
										<Drawer.Title w="100%">
											<Flex justify="space-between" align="center">
												<Flex wrap="nowrap" align="center" gap={16}>
													<Avatar
														src="/Avatar.png"
														alt="avatar"
														size={48}
														radius="xl"
													/>
													<Stack gap={0}>
														<Text size="xl" fw={700}>
															{resume.result.name}
														</Text>
														<Text size="sm" c="gray.7">
															{resume.result.age
																? `${resume.result.age}歳`
																: "-"}
															/{" "}
															{resume.result.sex
																? SEX_PHRASE[resume.result.sex]
																: "-"}
															/ {resume.result.address ?? "-"}
														</Text>
													</Stack>
												</Flex>
												<Stack
													w={28}
													h={28}
													justify="center"
													align="center"
													style={{
														cursor: "pointer",
														backgroundColor: "transparent",
														borderRadius: "4px",
													}}
													onMouseEnter={(e) => {
														e.currentTarget.style.backgroundColor =
															theme.colors.gray[0];
													}}
													onMouseLeave={(e) => {
														e.currentTarget.style.backgroundColor =
															"transparent";
													}}
													color={theme.colors.gray[7]}
													onClick={
														aiDocumentSelectionResultCloseConfirmationDialogOpen
													}
												>
													<CloseIcon size="20" />
												</Stack>
											</Flex>
										</Drawer.Title>
									</Drawer.Header>
									<Drawer.Body px={0}>
										<RecruiteeDetail resume={resume} />
									</Drawer.Body>
									<AiDocumentSelectionResultCloseConfirmationDialog
										opened={
											aiDocumentSelectionResultCloseConfirmationDialogOpened
										}
										onClose={
											aiDocumentSelectionResultCloseConfirmationDialogClose
										}
										handleCloseAiDocumentSelectionResult={
											handleRecruiteeDetailDrawerClose
										}
									/>
								</>
							) : (
								<>
									<Drawer.Header>
										<Drawer.Title w="100%">
											<Flex justify="space-between" align="center">
												<Flex wrap="nowrap" align="center" gap={16}>
													<Title order={2}>AI書類選考失敗</Title>
												</Flex>
												<Drawer.CloseButton
													onClick={handleRecruiteeDetailDrawerClose}
												/>
											</Flex>
										</Drawer.Title>
									</Drawer.Header>
									<Drawer.Body px={0}>
										<Flex justify="center" align="center" h="100%">
											<Text>AI書類選考に失敗しました。</Text>
										</Flex>
									</Drawer.Body>
								</>
							)}
						</Drawer.Content>
					</Drawer.Root>
				</Stack>
			</Container>
			<RecruitPositionGenerateModal
				opened={recruitPositionGenerateModalOpened}
				close={recruitPositionGenerateModalClose}
				handleRefetch={refetchJobInformation}
			/>
		</>
	);
};
